/**
 * Canonical analytics event names. Centralized so dashboards/funnels in
 * Mixpanel bind to a stable vocabulary and typos can't silently fork an event.
 *
 * Keep names snake_case, past-tense, and feature-centric — they answer
 * "what did the admin do?" not "what did the code do?".
 */
export const AnalyticsEvent = {
  /** SPA route change (path normalized to a template). Fired centrally. */
  PageViewed: "page_viewed",
  /** Admin clicked "Sign in with Cognito". */
  LoginStarted: "login_started",
  /** A member was invited (single). Props: role_count, is_multi_role, roles. */
  MemberInvited: "member_invited",
  /** A pending member's invite was re-sent. */
  InviteResent: "invite_resent",
  /** A user was soft-deleted (respai_admin only). */
  MemberDeleted: "member_deleted",
  /** An incident report PDF was opened inline. */
  ReportPdfViewed: "report_pdf_viewed",
  /** An incident report PDF was downloaded (attachment). */
  ReportPdfDownloaded: "report_pdf_downloaded",
  /** Incidents were exported to CSV. Props: count. */
  IncidentsCsvExported: "incidents_csv_exported",
  /** A compliance archive ZIP was downloaded for a date range. */
  ComplianceArchiveDownloaded: "compliance_archive_downloaded",
  /** A platform admin switched the active department. Props: scope. */
  DepartmentSwitched: "department_switched",
  /** The SOP-compliance preview was opened (past the role gate). */
  SopReportViewed: "sop_report_viewed",
  /** The chief left the SOP-compliance preview. Props: seconds (dwell time). */
  SopReportExited: "sop_report_exited",
  /** A section of the SOP report scrolled into view. Props: section. */
  SopSectionViewed: "sop_section_viewed",
  /** The SOP report was printed / saved to PDF via "Download PDF". */
  SopPdfDownloaded: "sop_pdf_downloaded",
  /** A flagged-response incident link was followed from the SOP report. */
  SopIncidentOpened: "sop_incident_opened",
  /** "Was this useful?" was answered on the SOP report. Props: useful. */
  SopFeedbackSubmitted: "sop_feedback_submitted",
  /** The "email us what's missing" feedback link was opened. */
  SopFeedbackEmailOpened: "sop_feedback_email_opened",
  /** An SOP/SOG document finished its upload (ticket → PUT → confirm). Props: size_kb. */
  SopDocumentUploaded: "sop_document_uploaded",
  /** An extracted SOP rule was approved for scoring (the human gate). Props: category. */
  SopRuleApproved: "sop_rule_approved",
  /** An extracted SOP rule was rejected in review. Props: category. */
  SopRuleRejected: "sop_rule_rejected",
} as const;

export type AnalyticsEventName =
  (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];
